import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { DashboardAPI, PendingVisit } from "../../services/dashboard.service";
import { DiagnosisAPI } from "../../services/diagnosis.service";
import { OnboardingAPI } from "../../services/onboarding.service";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function daysDiffFromToday(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  const diffTime = today.getTime() - target.getTime();
  return Math.max(0, Math.round(diffTime / (1000 * 60 * 60 * 24)));
}

const VISIT_STEP_LABELS: Record<string, string> = {
  "90sdiagnosis": "Diagnosis pad",
  "soap-diagnosis-pad": "SOAP note",
  "continue-diagnosis": "Prescription",
};

const VISIT_CONTINUE_LABELS: Record<string, string> = {
  "90sdiagnosis": "Continue diagnosis",
  "soap-diagnosis-pad": "Continue SOAP note",
  "continue-diagnosis": "Continue prescription",
};

const PendingVisitsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("view") === "all" ? "all" : "drafts";

  const [lockedDoctorId, setLockedDoctorId] = useState<string | null>(null);
  const [visits, setVisits] = useState<PendingVisit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Filters & search
  const [ageFilter, setAgeFilter] = useState<"" | "today" | "older">("");
  const [statusFilter, setStatusFilter] = useState<"" | "Draft" | "Finalized">("");
  const [search, setSearch] = useState("");

  // Toast
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage((cur) => (cur === msg ? null : cur));
    }, 3000);
  };

  const loadData = () => {
    setLoading(true);
    DashboardAPI.getPendingVisits()
      .then((data) => {
        setVisits(data);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    OnboardingAPI.getMe()
      .then((me) => {
        if (me.role === "Doctor" && me.doctor_id) {
          setLockedDoctorId(me.doctor_id);
        }
      })
      .catch(() => {});
  }, []);

  // Filter drafts scoped to doctor
  const scopedDrafts = useMemo(() => {
    if (!lockedDoctorId) return visits;
    return visits.filter((v) => !v.doctor_id || v.doctor_id === lockedDoctorId);
  }, [visits, lockedDoctorId]);

  // Split into today vs older
  const { todayDrafts, olderDrafts } = useMemo(() => {
    const today: PendingVisit[] = [];
    const older: PendingVisit[] = [];
    scopedDrafts.forEach((v) => {
      const days = daysDiffFromToday(v.visit_date || (v as any).created_at || new Date().toISOString());
      if (days === 0) {
        today.push(v);
      } else {
        older.push(v);
      }
    });
    return { todayDrafts: today, olderDrafts: older };
  }, [scopedDrafts]);

  // Filtered list based on search and age filter
  const displayedDrafts = useMemo(() => {
    let list = scopedDrafts;
    if (ageFilter === "today") list = todayDrafts;
    if (ageFilter === "older") list = olderDrafts;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (v) =>
          v.patient_name.toLowerCase().includes(q) ||
          (v.chief_complaint && v.chief_complaint.toLowerCase().includes(q))
      );
    }
    return list;
  }, [scopedDrafts, todayDrafts, olderDrafts, ageFilter, search]);

  // Actions
  const handleFinalize = async (v: PendingVisit) => {
    try {
      await DiagnosisAPI.finalizeVisit(v.visit_id);
      showToast(`${v.patient_name} — visit finalized`);
      loadData();
    } catch {
      showToast("Failed to finalize visit");
    }
  };

  const handleContinue = (v: PendingVisit) => {
    const step = v.current_step || "90sdiagnosis";
    navigate(`/${step}?visit_id=${v.visit_id}&patient_id=${v.patient_id}&doctor_id=${v.doctor_id}`);
  };

  return (
    <div className="min-h-screen bg-[#f7f8fc] flex font-sans">
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translate(-50%, 12px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
      <Sidebar />

      <div className="flex-1 min-w-0 flex flex-col lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out">
        <AppHeader title="Visits" />

        <div className="max-w-[1440px] mx-auto w-full p-4 sm:p-6 flex flex-col gap-5 pt-16 lg:pt-6">
          {/* ── Top Header & Tab Toggle ─────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-4 flex-wrap mb-[-4px]">
            <div>
              <h1 className="text-[20px] font-bold text-[#1e293b] leading-tight">Visits</h1>
              <p className="text-[14px] text-[#94a3b8] mt-0.5">
                {scopedDrafts.length} draft visit{scopedDrafts.length !== 1 ? "s" : ""} · Visits that have not been finalized, click a row to continue.
              </p>
            </div>

            {/* Three-way View Toggle (Start Visit · All Visits · Drafts) */}
            <div
              className="flex items-center bg-[#f1f5f9] p-1 rounded-xl gap-0.5"
              role="tablist"
              aria-label="Visit views"
            >
              <Link
                to="/start-visit"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-[#64748b] hover:text-[#1e293b] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 2v2M5 2v2M5 3H4a2 2 0 00-2 2v4a6 6 0 0012 0V5a2 2 0 00-2-2h-1" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 15a6 6 0 0012 0v-3" />
                  <circle cx="20" cy="10" r="2" />
                </svg>
                Start Visit
              </Link>

              <button
                type="button"
                onClick={() => setSearchParams({ view: "all" })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] transition-colors cursor-pointer ${
                  viewMode === "all"
                    ? "font-semibold bg-white text-[#1e293b] shadow-sm"
                    : "font-medium text-[#64748b] hover:text-[#1e293b]"
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M16 13H8M16 17H8" />
                </svg>
                All Visits
              </button>

              <button
                type="button"
                onClick={() => setSearchParams({})}
                aria-current={viewMode === "drafts" ? "page" : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] transition-colors cursor-pointer ${
                  viewMode === "drafts"
                    ? "font-semibold bg-white text-[#1e293b] shadow-sm"
                    : "font-medium text-[#64748b] hover:text-[#1e293b]"
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4Z" />
                </svg>
                Drafts
                {scopedDrafts.length > 0 && (
                  <span className="min-w-[18px] h-[18px] px-[5px] rounded-full bg-[#ea580c] text-white text-[11px] font-bold inline-flex items-center justify-center">
                    {scopedDrafts.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── Stat Filter Chips Row ─────────────────────────────────────────── */}
          {viewMode === "drafts" ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAgeFilter(ageFilter === "today" ? "" : "today")}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl border text-[12px] font-medium cursor-pointer transition-all shadow-sm ${
                  ageFilter === "today"
                    ? "bg-[#d97706] text-white border-[#d97706]"
                    : "bg-[#fffbeb] text-[#d97706] border-[#fef3c7] hover:shadow"
                } ${ageFilter && ageFilter !== "today" ? "opacity-55" : "opacity-100"}`}
              >
                <span className="text-[18px] font-bold leading-none">{todayDrafts.length}</span>
                <span>Started today</span>
                {ageFilter === "today" && (
                  <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>

              <button
                type="button"
                onClick={() => setAgeFilter(ageFilter === "older" ? "" : "older")}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl border text-[12px] font-medium cursor-pointer transition-all shadow-sm ${
                  ageFilter === "older"
                    ? "bg-[#ea580c] text-white border-[#ea580c]"
                    : "bg-[#fff7ed] text-[#ea580c] border-[#fed7aa] hover:shadow"
                } ${ageFilter && ageFilter !== "older" ? "opacity-55" : "opacity-100"}`}
              >
                <span className="text-[18px] font-bold leading-none">{olderDrafts.length}</span>
                <span>Older than today</span>
                {ageFilter === "older" && (
                  <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === "Draft" ? "" : "Draft")}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl border text-[12px] font-medium cursor-pointer transition-all shadow-sm ${
                  statusFilter === "Draft"
                    ? "bg-[#ea580c] text-white border-[#ea580c]"
                    : "bg-[#fff7ed] text-[#ea580c] border-[#fed7aa] hover:shadow"
                } ${statusFilter && statusFilter !== "Draft" ? "opacity-55" : "opacity-100"}`}
              >
                <span className="text-[18px] font-bold leading-none">{scopedDrafts.length}</span>
                <span>Draft</span>
                {statusFilter === "Draft" && (
                  <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>

              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === "Finalized" ? "" : "Finalized")}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl border text-[12px] font-medium cursor-pointer transition-all shadow-sm ${
                  statusFilter === "Finalized"
                    ? "bg-[#16a34a] text-white border-[#16a34a]"
                    : "bg-[#f0fdf4] text-[#16a34a] border-[#dcfce7] hover:shadow"
                } ${statusFilter && statusFilter !== "Finalized" ? "opacity-55" : "opacity-100"}`}
              >
                <span className="text-[18px] font-bold leading-none">0</span>
                <span>Finalized</span>
                {statusFilter === "Finalized" && (
                  <svg className="w-3.5 h-3.5 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          )}

          {/* ── Main Visits Card ──────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-[#f1f5f9] shadow-sm overflow-hidden">
            {/* Search Bar */}
            <div className="px-4 py-3 border-b border-[#f1f5f9] flex items-center justify-between gap-3 flex-wrap min-h-[57px] bg-white">
              <div className="relative flex-1 sm:max-w-xs min-w-[180px]">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94a3b8]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <circle cx="11" cy="11" r="8" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search patient or complaint…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-[#e2e8f0] text-[13px] text-[#334155] outline-none focus:border-[#93c5fd] focus:ring-2 focus:ring-[#dbeafe] bg-white"
                />
              </div>
            </div>

            {/* List Body */}
            {loading && (
              <div className="py-20 text-center text-[#94a3b8] text-[14px]">Loading drafts…</div>
            )}

            {!loading && error && (
              <div className="py-20 text-center text-[#dc2626] text-[14px]">
                Failed to load draft visits.
              </div>
            )}

            {!loading && !error && displayedDrafts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center text-[#94a3b8] gap-3">
                <svg className="w-12 h-12 opacity-25" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4Z" />
                </svg>
                <p className="text-[14px] text-[#94a3b8]">
                  {search
                    ? "No drafts match your search"
                    : ageFilter
                    ? "No drafts match this filter"
                    : "No draft visits — everything is finalized"}
                </p>
                <Link to="/start-visit" className="text-[13px] font-semibold text-[#0891b2] hover:text-[#0e7490]">
                  Back to Start Visit
                </Link>
              </div>
            )}

            {!loading && !error && displayedDrafts.length > 0 && (
              <div className="flex flex-col divide-y divide-[#f8fafc]">
                {/* Older Than Today Section */}
                {displayedDrafts.filter((v) => daysDiffFromToday(v.visit_date || (v as any).created_at) > 0).length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-[#f8fafc] text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wider border-b border-[#f1f5f9]">
                      Older than today · {displayedDrafts.filter((v) => daysDiffFromToday(v.visit_date || (v as any).created_at) > 0).length}
                    </div>
                    {displayedDrafts
                      .filter((v) => daysDiffFromToday(v.visit_date || (v as any).created_at) > 0)
                      .map((v) => {
                        const days = daysDiffFromToday(v.visit_date || (v as any).created_at);
                        const stepLabel = VISIT_STEP_LABELS[v.current_step] || "Diagnosis pad";
                        const continueLabel = VISIT_CONTINUE_LABELS[v.current_step] || "Continue visit";
                        return (
                          <div
                            key={v.visit_id}
                            className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-l-4 border-l-[#fdba74] bg-white hover:bg-[#fafbfd] transition-colors"
                          >
                            <div className="w-16 flex-shrink-0">
                              <div className="text-[14px] font-bold text-[#1e293b] leading-tight">
                                {formatTime(v.visit_date)}
                              </div>
                              <div className="text-[11px] text-[#94a3b8] mt-0.5">
                                {formatDate(v.visit_date).split(" ").slice(0, 2).join(" ")}
                              </div>
                            </div>
                            <div className="flex-1 min-w-[200px]">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[14px] font-semibold text-[#1e293b]">
                                  {v.patient_name}
                                </span>
                                <span className="text-[11px] text-[#94a3b8] font-mono">
                                  {v.patient_id?.slice(0, 8)}
                                </span>
                                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#ffedd5] text-[#c2410c]">
                                  Open {days} day{days !== 1 ? "s" : ""}
                                </span>
                              </div>
                              <div className="text-[12px] text-[#64748b] mt-0.5">
                                {v.chief_complaint || "No chief complaint recorded"}
                              </div>
                              <div className="text-[12px] text-[#94a3b8] mt-0.5">
                                Stopped at {stepLabel}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                              <button
                                type="button"
                                onClick={() => handleFinalize(v)}
                                className="px-3 min-h-[36px] rounded-lg text-[13px] font-semibold text-[#15803d] bg-white border border-[#bbf7d0] hover:bg-[#f0fdf4] transition cursor-pointer"
                              >
                                Finalize
                              </button>
                              <button
                                type="button"
                                onClick={() => handleContinue(v)}
                                className="px-3.5 min-h-[36px] rounded-lg text-[13px] font-semibold text-white bg-[#2563eb] hover:bg-[#1d4ed8] transition cursor-pointer shadow-sm"
                              >
                                {continueLabel}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </>
                )}

                {/* Started Today Section */}
                {displayedDrafts.filter((v) => daysDiffFromToday(v.visit_date || (v as any).created_at) === 0).length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-[#f8fafc] text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wider border-b border-[#f1f5f9]">
                      Started today · {displayedDrafts.filter((v) => daysDiffFromToday(v.visit_date || (v as any).created_at) === 0).length}
                    </div>
                    {displayedDrafts
                      .filter((v) => daysDiffFromToday(v.visit_date || (v as any).created_at) === 0)
                      .map((v) => {
                        const stepLabel = VISIT_STEP_LABELS[v.current_step] || "Diagnosis pad";
                        const continueLabel = VISIT_CONTINUE_LABELS[v.current_step] || "Continue visit";
                        return (
                          <div
                            key={v.visit_id}
                            className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-l-4 border-l-[#fbbf24] bg-white hover:bg-[#fafbfd] transition-colors"
                          >
                            <div className="w-16 flex-shrink-0">
                              <div className="text-[14px] font-bold text-[#1e293b] leading-tight">
                                {formatTime(v.visit_date)}
                              </div>
                              <div className="text-[11px] text-[#94a3b8] mt-0.5">Today</div>
                            </div>
                            <div className="flex-1 min-w-[200px]">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[14px] font-semibold text-[#1e293b]">
                                  {v.patient_name}
                                </span>
                                <span className="text-[11px] text-[#94a3b8] font-mono">
                                  {v.patient_id?.slice(0, 8)}
                                </span>
                                <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#b45309]">
                                  Open today
                                </span>
                              </div>
                              <div className="text-[12px] text-[#64748b] mt-0.5">
                                {v.chief_complaint || "No chief complaint recorded"}
                              </div>
                              <div className="text-[12px] text-[#94a3b8] mt-0.5">
                                Stopped at {stepLabel}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                              <button
                                type="button"
                                onClick={() => handleFinalize(v)}
                                className="px-3 min-h-[36px] rounded-lg text-[13px] font-semibold text-[#15803d] bg-white border border-[#bbf7d0] hover:bg-[#f0fdf4] transition cursor-pointer"
                              >
                                Finalize
                              </button>
                              <button
                                type="button"
                                onClick={() => handleContinue(v)}
                                className="px-3.5 min-h-[36px] rounded-lg text-[13px] font-semibold text-white bg-[#2563eb] hover:bg-[#1d4ed8] transition cursor-pointer shadow-sm"
                              >
                                {continueLabel}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Toast ──────────────────────────────────────────────────────── */}
        {toastMessage && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2.5 px-4 py-3 rounded-lg bg-white text-[#334155] text-[14px] border-l-4 border-l-[#22c55e] shadow-lg [animation:toastIn_300ms_ease] max-w-[calc(100vw-32px)]">
            <svg className="w-[18px] h-[18px] text-[#22c55e] flex-shrink-0 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
            </svg>
            <span>{toastMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingVisitsPage;
